import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useGoBack } from "./history";
import { useNative } from "./media";

export interface PhoneBack {
  /** The parent screen. Back steps through history when that is where the person came from. */
  to: string;
  /** The parent's name. A long one reads "Back", the way the phone does it; "" shows the chevron alone. */
  label?: string;
  /** Used instead of going to `to`, for a back that must ask first (unsaved edits). */
  onBack?: () => void;
}

/**
 * What ends a main screen's title row, given by the app around the screens: the owner app's Flashsuite menu
 * on a phone; the partner portal has none.
 */
export const TitleEnd = createContext<ReactNode>(null);

const LONGEST_BACK_LABEL = 12;
/** Past this many letters between the back label and the title, the bar has room only for "Back". */
const BAR_LETTERS = 30;

/**
 * The one bar at the top of a screen on a phone: back on the left, the title in the middle and up to two
 * actions on the right. A main screen (`large`) shows its title big under the bar, and the bar takes the
 * title once it scrolls away. A main screen with nowhere to go back to keeps its buttons on the title's own
 * line until then, so they never float alone over an empty bar, and ends that line with `TitleEnd`.
 * Drawn on a phone and an iPad; a computer keeps the page's own header.
 */
export function PhoneNav({
  title,
  back,
  left,
  actions,
  large = false,
  sub,
}: {
  title: ReactNode;
  back?: PhoneBack;
  /** A bar button on the left, in place of back (Cancel in a flow). On a main screen it sits beside the title. */
  left?: ReactNode;
  actions?: ReactNode;
  large?: boolean;
  /** One line under a large title. */
  sub?: ReactNode;
}) {
  const on = useNative();
  const end = useContext(TitleEnd);
  const goBack = useGoBack();
  const bar = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!on) return;
    const scroller = bar.current?.closest(".pn-pane, .pn-main");
    if (!scroller) return;
    const read = () => setScrolled(scroller.scrollTop > (large ? 40 : 2));
    read();
    scroller.addEventListener("scroll", read, { passive: true });
    return () => scroller.removeEventListener("scroll", read);
  }, [on, large]);

  if (!on) return null;
  // A large title is out of the bar until it scrolls away, so the label has the room then.
  const titleLetters = large ? 0 : typeof title === "string" ? title.length : 14;
  const backLabel =
    back?.label === ""
      ? ""
      : back?.label && back.label.length <= LONGEST_BACK_LABEL && back.label.length + titleLetters <= BAR_LETTERS
        ? back.label
        : "Back";
  const inline = large && !back;
  const onTitleLine = inline && !scrolled;
  const cls = ["pn-nav", scrolled ? "scrolled" : "", large ? "large" : "always", inline ? "bare" : ""].filter(Boolean).join(" ");

  return (
    <>
      <div className={cls} ref={bar}>
        <div className="pn-nav-row">
          <div className="pn-nav-l">
            {onTitleLine
              ? null
              : (left ??
                (back ? (
                  // A link, so a page that guards leaving sees it as one.
                  <Link
                    to={back.to}
                    className="pn-back"
                    onClick={(e) => {
                      e.preventDefault();
                      if (back.onBack) back.onBack();
                      else goBack(back.to);
                    }}
                    aria-label={back.label ? `Back to ${back.label}` : "Back"}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M15 5l-7 7 7 7" />
                    </svg>
                    {backLabel ? <span>{backLabel}</span> : null}
                  </Link>
                ) : null))}
          </div>
          <div className="pn-nav-t">{title}</div>
          <div className="pn-nav-r">{onTitleLine ? null : actions}</div>
        </div>
      </div>
      {large ? (
        <div className="pn-large">
          <div className="pn-large-row">
            <h1>{title}</h1>
            {onTitleLine && (left || actions) ? (
              <div className="pn-large-acts">
                {left}
                {actions}
              </div>
            ) : null}
            {back ? null : end}
          </div>
          {sub ? <p>{sub}</p> : null}
        </div>
      ) : null}
    </>
  );
}
